import React from 'react';
import { observer } from 'mobx-react-lite';
import { Star } from 'lucide-react';
import reviewsViewModel from '../../view-model/professional/reviews-view-model';
import { useTranslation } from 'react-i18next';

interface ReviewStatsProps {
  isLoading?: boolean;
}

const ReviewStatsComponent: React.FC<ReviewStatsProps> = observer(({ isLoading = false }) => {
  const { t } = useTranslation();
  const stats = reviewsViewModel.stats;
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6"></div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <p className="text-gray-500 dark:text-gray-400">{t("reviews.noStats")}</p>
      </div>
    );
  }

  // Fonction pour générer les étoiles
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={18}
            className={`${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  // Calculer le pourcentage pour chaque note
  const calculatePercentage = (count: number) => {
    return stats.count > 0 ? Math.round((count / stats.count) * 100) : 0;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
        {t("reviews.stats")}
      </h3>
      
      <div className="flex items-center mb-6">
        <div className="text-3xl font-bold text-gray-900 dark:text-white mr-3">
          {stats.averageRating.toFixed(1)}
        </div>
        <div className="flex flex-col">
          {renderStars(stats.averageRating)}
          <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {stats.count} {t("reviews.totalReviews")}
          </span>
        </div>
      </div>

      {/* Distribution des notes */}
      <div className="space-y-3">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = stats.ratingDistribution?.[rating] || 0;
          const percentage = calculatePercentage(count);
          
          return (
            <div key={rating} className="flex items-center">
              <div className="flex items-center w-12">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-1">
                  {rating}
                </span>
                <Star size={14} className="text-yellow-400 fill-yellow-400" />
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mx-2">
                <div
                  className="bg-yellow-400 h-2.5 rounded-full"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              
              <div className="w-12 text-right">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {count}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default ReviewStatsComponent;
